// Per-column search: a header icon button (peer of the sort / filter
// buttons) that opens a popup with a single text input scoped to one
// column. Opt-in per column via `column_search:` in the Ruby config
// (round-trips to `columnSearch` in the columns JSON).
//
// This module owns the header button + popup: insertion, a11y wiring,
// open/close/focus management, and feeding the popup input into the
// controller's `filterState` so typing narrows the table (local mode) or
// drives a ransack `predicate` refetch (remote mode).
//
// The button is built here from scratch (a column can only have one native
// `headerPopup`, and the filter already claims it), but positioning is handed
// to Tabulator's own Popup engine — the same one behind the filter popups.
// `popup.show(button, 'bottom')` mounts the element on `document.body` with
// absolute *page* coordinates and flips it back on-screen at the viewport
// edges (`_fitToScreen`). Body-mounting escapes the container's `overflow:
// hidden`; absolute page coords (not `position: fixed`) mean the popup scrolls
// with the page and stays anchored to its button. We still drive
// open/close/focus ourselves rather than via Tabulator's `hideOnBlur` (see
// `openColumnSearchPopup`).

import { t } from '../../javascript/lib/i18n';
import { isFilterActive } from './filters';

const SEARCH_ICON_HTML =
  '<i class="fas fa-search fs-tiny" aria-hidden="true"></i>';

// For each opted-in leaf column, insert a search icon button into the
// header. Idempotent — skips a column whose button already exists, so a
// re-run (e.g. a second `tableBuilt`) won't duplicate buttons.
//
// Returns the number of buttons actually inserted. The caller uses this to
// decide whether to re-run Tabulator's layout: the button is a fixed-width
// box appended *after* the initial width measurement, so — unlike the native
// filter icon, which Tabulator renders (and measures) itself — its width
// isn't reflected in the column's content-measured width. On a table with no
// body cells to widen the column past its title, that leaves the header too
// narrow and the sort arrow and search icon overlap. Re-measuring with the
// button present (see the `tableBuilt` handler) sizes the column to fit it.
export function installColumnSearchButtons(containerEl, controller) {
  let inserted = 0;
  containerEl
    .querySelectorAll('.tabulator-col[tabulator-field]')
    .forEach((colEl) => {
      const field = colEl.getAttribute('tabulator-field');
      const col = controller.leafColumns.find((c) => c.field === field);
      if (!col || !col.columnSearch) return;
      if (colEl.querySelector('.amba-tabulator-col-search-button')) return;

      // Remote mode filters server-side via the column's ransack `predicate`.
      // Without one, the box would still render, light up "active", and reset
      // the page on every keystroke, yet send nothing to the server — a
      // silent no-op that looks like a working search. Skip the button (and
      // warn) rather than ship that misleading control; local mode ignores
      // `predicate:` and matches on cell text, so it's unaffected.
      if (controller.remoteValue && !col.columnSearch.predicate) {
        console.warn(
          `[tabulator-table] column "${field}" opts into column_search in ` +
            'remote mode but declares no `predicate:` — no search button is ' +
            'rendered because it could not filter server-side. Add ' +
            '`column_search: { predicate: "<attr>_cont" }`.'
        );
        return;
      }

      const title =
        colEl.querySelector('.tabulator-col-title')?.textContent.trim() ||
        field;
      const button = buildSearchButton(title);

      // Sit alongside the sort / filter buttons at the `.tabulator-col-content`
      // level (the filter button is lifted here by `liftFilterButtons`), so
      // header flex lays them out as [title] [sort] [filter] [search].
      const colContent = colEl.querySelector('.tabulator-col-content');
      (colContent || colEl).appendChild(button);

      const open = () =>
        openColumnSearchPopup(field, title, button, controller);
      button.addEventListener('click', open);
      button.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        open();
      });

      inserted += 1;
    });

  return inserted;
}

// Tabulator renders its own header buttons as plain <span>s and upgrades
// them to ARIA buttons (see `liftFilterButtons`). We mirror that so the
// search button is keyboard-focusable and announced as a dialog trigger.
function buildSearchButton(title) {
  const btn = document.createElement('span');
  btn.className = 'amba-tabulator-col-search-button';
  btn.setAttribute('role', 'button');
  btn.setAttribute('tabindex', '0');
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute(
    'aria-label',
    t('js.tabulator_table.search_column', { title })
  );
  btn.innerHTML = SEARCH_ICON_HTML;
  return btn;
}

// Open the popup for a column. Clicking a button whose popup is already
// open toggles it closed; opening one closes any other open popup first,
// so at most one column-search popup is ever mounted.
function openColumnSearchPopup(field, title, button, controller) {
  if (controller._columnSearchPopup?.field === field) {
    closeColumnSearchPopup(controller, { restoreFocus: true });
    return;
  }
  closeColumnSearchPopup(controller);

  const el = buildColumnSearchPopup(field, title, controller);

  // Hand positioning to Tabulator's Popup engine: `show(button, 'bottom')`
  // mounts `el` on `document.body`, positions it below the button in absolute
  // page coords, and runs `_fitToScreen` — flipping to the right/top edge when
  // it would overflow the viewport (the fix for right-edge columns rendering
  // off-screen). We keep our own open/close/focus handling below instead of
  // Tabulator's `hideOnBlur`: our button-guarded outside-click preserves
  // click-to-toggle, and Tabulator's Escape handler is a no-op (it compares
  // `e.key` to a legacy keyCode — see the note in `filters.js`).
  const popup = controller.tabulator.module('popup').popup(el);
  popup.show(button, 'bottom');
  button.setAttribute('aria-expanded', 'true');

  const input = el.querySelector('.amba-tabulator-col-search-input');
  input?.focus();

  // Outside-click closes the popup. Guard against the button itself so its
  // own click handler can run the toggle (mousedown fires before click).
  const onDocMouseDown = (e) => {
    if (el.contains(e.target) || button.contains(e.target)) return;
    closeColumnSearchPopup(controller);
  };
  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    closeColumnSearchPopup(controller, { restoreFocus: true });
  };
  document.addEventListener('mousedown', onDocMouseDown);
  el.addEventListener('keydown', onKeyDown);

  controller._columnSearchPopup = {
    field,
    button,
    cleanup() {
      document.removeEventListener('mousedown', onDocMouseDown);
      el.removeEventListener('keydown', onKeyDown);
      // Silent hide — we never registered a blur callback; this detaches the
      // element from the DOM (Tabulator's Popup owns the lifecycle now).
      popup.hide(true);
      button.setAttribute('aria-expanded', 'false');
    },
  };
}

// Tear down the open popup (if any), removing its listeners and element.
// Exported so the controller can call it on `disconnect` (per
// stimulus/memory-management) and reuse it wherever a close is needed.
export function closeColumnSearchPopup(
  controller,
  { restoreFocus = false } = {}
) {
  const current = controller._columnSearchPopup;
  if (!current) return;
  const { button } = current;
  current.cleanup();
  controller._columnSearchPopup = null;
  if (restoreFocus) button?.focus();
}

function buildColumnSearchPopup(field, title, controller) {
  // Seed (or reuse) this column's search state on the controller so the
  // query survives the popup being closed and re-opened, and so
  // `applyFilters` / `isFilterActive` can read it like any other filter.
  const state = controller.getOrInitFilterState(field, 'search', () => ({
    kind: 'search',
    query: '',
  }));

  const el = document.createElement('div');
  // Tabulator's Popup adds `.tabulator-popup-container` on show; the
  // `-col-search-popup` class scopes our dropdown chrome to that container
  // (its own shell rather than the filter popup's, whose padding/gap is tuned
  // for a multi-row layout, not a lone input).
  el.className = 'amba-tabulator-col-search-popup';
  el.setAttribute('role', 'dialog');
  el.setAttribute(
    'aria-label',
    t('js.tabulator_table.search_column', { title })
  );
  // Appended to <body>, so its input bubbles to the body-level
  // confirm-navigation controller — opt out so typing doesn't mark the
  // page dirty (matches the filter popups).
  el.dataset.confirmNavigationIgnoreInputWithin = '';

  const input = document.createElement('input');
  input.type = 'search';
  input.className =
    'form-control form-control-sm amba-tabulator-col-search-input';
  input.placeholder = t('js.common.search_placeholder');
  input.setAttribute(
    'aria-label',
    t('js.tabulator_table.search_column', { title })
  );
  // Rehydrate from state so reopening a popup shows the active query.
  input.value = state.query;

  // Local mode applies on every keystroke (cheap in-memory refilter);
  // remote mode debounces — `applyFiltersDebounced` picks the right path.
  input.addEventListener('input', () => {
    state.query = input.value;
    controller.applyFiltersDebounced();
  });

  el.append(input);
  return el;
}

// Toggle the active visual state on each column-search button so users can
// see which columns currently have a query applied. Mirrors
// `refreshFilterButtonStates` for the filter buttons; driven by the
// controller's per-column `filterState` Map.
export function refreshColumnSearchButtonStates(containerEl, filterState) {
  containerEl
    .querySelectorAll('.tabulator-col[tabulator-field]')
    .forEach((col) => {
      const field = col.getAttribute('tabulator-field');
      const btn = col.querySelector('.amba-tabulator-col-search-button');
      if (!btn) return;
      // Read only the `search` criteria — a column may also hold a checkbox /
      // number filter, but that lights the filter button, not this one.
      btn.classList.toggle(
        'amba-tabulator-search-active',
        isFilterActive(filterState.get(field)?.get('search'))
      );
    });
}
